import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import MeasurementItem from './MeasurementItem';
import ApiServices from '../../../../app/src/shared/ApiServices/ApiServices';
import { toast } from 'react-toastify';
import { getQueryParam } from "../../../../app/src/utils/queryParams"

const MeasurementTable = ({
    data,
    title,
    onClick,
    onEdit,
    onDelete,
    servicesManager,
}: withAppTypes) => {
    const { customizationService, measurementService } = servicesManager.services;
    const { t } = useTranslation('MeasurementTable');
    const amount = data.length;

    const itemCustomization = customizationService.getCustomization('MeasurementItem', {
        content: MeasurementItem,
        contentProps: {},
    });
    const CustomMeasurementItem = itemCustomization.content;
    const patientId = getQueryParam('studyId');
    const [deletingUids, setDeletingUids] = useState<string[]>([]);

    const onMeasurementDeleteHandler = async ({ uid }) => {
        const measurement = measurementService.getMeasurement(uid);

        if (!measurement) {
            onDelete?.({ uid });
            return;
        }

        try {
            if (measurement.isFromAPI) {
                setDeletingUids(prev => [...prev, uid]);
                const response = await ApiServices.deleteDiagnostic(patientId, uid);
                toast.success(response?.message);
            }

            // Local state/UI sync
            onDelete?.({ uid });
            measurementService.remove(uid, { ...measurement }, true);
        } catch (error) {
            toast.error(error?.response?.data?.message);
        } finally {
            if (measurement.isFromAPI) {
                setDeletingUids(prev => prev.filter(id => id !== uid));
            }
        }
    };

    return (
        <div>
            <div className="bg-secondary-main flex justify-between px-2 py-1">
                <span className="text-base font-bold uppercase tracking-widest text-white">{t(title)}</span>
                <span className="text-base font-bold text-white">{amount}</span>
            </div>
            <div className="ohif-scrollbar max-h-112 overflow-hidden">
                {data.length !== 0 &&
                    data.map((measurementItem, index) => (
                        <CustomMeasurementItem
                            key={measurementItem.uid}
                            uid={measurementItem.uid}
                            index={index + 1}
                            label={measurementItem.label}
                            isActive={measurementItem.isActive}
                            displayText={measurementItem.displayText}
                            item={measurementItem}
                            onClick={onClick}
                            onEdit={onEdit}
                            onDelete={onMeasurementDeleteHandler}
                            disabled={deletingUids.includes(measurementItem.uid)}
                        />
                    ))}
                {data.length === 0 && (
                    <div className="group flex cursor-default border border-transparent bg-black transition duration-300">
                        <div className="bg-primary-dark text-primary-light group-hover:bg-secondary-main w-6 py-1 text-center text-base transition duration-300"></div>
                        <div className="flex flex-1 items-center justify-between px-2 py-4">
                            <span className="text-primary-light mb-1 flex flex-1 items-center text-base">
                                {t('No tracked measurements')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

MeasurementTable.defaultProps = {
    data: [],
    onClick: () => { },
    onEdit: () => { },
};

MeasurementTable.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            displayText: PropTypes.arrayOf(PropTypes.string),
            isActive: PropTypes.bool,
        })
    ),
    onClick: PropTypes.func,
    onEdit: PropTypes.func,
};

export default MeasurementTable;
